/**
 * Background music hook — global singleton, shuffled playlist.
 *
 * Login-sidan skickar en specifik src → spelar den låten.
 * Appen (utan src) → shufflad spellista, nästa låt vid onended.
 */
import { useState, useEffect } from 'react'

const MUSIC_MUTED_KEY  = 'nq_music_muted'
const SHUFFLE_KEY      = 'nq_music_shuffle'

export const TRACKS = [
  { src: '/sounds/Carpe_Diem.mp3',    name: 'Carpe Diem'    },
  { src: '/sounds/Carefree.mp3',      name: 'Carefree'      },
  { src: '/sounds/Hyperfun.mp3',      name: 'Hyperfun'      },
  { src: '/sounds/Life_of_Riley.mp3', name: 'Life of Riley' },
  { src: '/sounds/Fretless.mp3',      name: 'Fretless'      },
]

// ── Singleton state ──────────────────────────────────────────────────────────
let _audio            = null
let _playlist         = []          // shuffled order of indices
let _playlistPos      = 0           // current position in _playlist
let _currentTrackIdx  = 0           // index into TRACKS
let _playlistMode     = false       // true when using shuffled playlist
let _visibilityAttached = false
let _listeners        = new Set()   // mute-state subscribers
let _trackListeners   = new Set()   // track-change subscribers

// ── Helpers ──────────────────────────────────────────────────────────────────
function fisherYates(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function notifyMute(muted)  { _listeners.forEach(fn => fn(muted)) }
function notifyTrack(idx)   { _trackListeners.forEach(fn => fn(idx)) }

export function isMusicMuted() {
  try { return localStorage.getItem(MUSIC_MUTED_KEY) !== 'false' } catch { return true }
}
export function isShuffleOn() {
  try { return localStorage.getItem(SHUFFLE_KEY) !== 'false' } catch { return true }
}

function buildPlaylist(startIdx = null) {
  let order = fisherYates(TRACKS.map((_, i) => i))
  // Ensure current track isn't first after reshuffle
  if (startIdx !== null && order[0] === startIdx && order.length > 1) {
    [order[0], order[1]] = [order[1], order[0]]
  }
  _playlist    = order
  _playlistPos = 0
}

function advanceTrack() {
  if (!_playlistMode) return
  _playlistPos = (_playlistPos + 1) % _playlist.length
  if (_playlistPos === 0) buildPlaylist(_currentTrackIdx) // reshuffle when list exhausted
  _currentTrackIdx = _playlist[_playlistPos]
  if (_audio) {
    _audio.src = TRACKS[_currentTrackIdx].src
    _audio.currentTime = 0
    if (!isMusicMuted()) _audio.play().catch(() => {})
  }
  notifyTrack(_currentTrackIdx)
}

function getOrCreateAudio(src) {
  if (!_audio) {
    _audio = new Audio(src)
    _audio.loop    = false
    _audio.volume  = 0.35
    _audio.muted   = isMusicMuted()
    _audio.onended = () => { if (_playlistMode) advanceTrack() }
  }
  return _audio
}

function ensureVisibilityListener() {
  if (_visibilityAttached) return
  _visibilityAttached = true
  document.addEventListener('visibilitychange', () => {
    if (!_audio) return
    if (document.hidden) {
      if (!_audio.paused) _audio.pause()
    } else {
      if (_audio.paused && !isMusicMuted()) _audio.play().catch(() => {})
    }
  })
}

function ensureAutoplayResume() {
  const resume = () => {
    if (_audio && !isMusicMuted()) {
      _audio.muted = false
      if (_audio.paused) _audio.play().catch(() => {})
    }
    ;['click','keydown','touchstart','pointerdown'].forEach(e =>
      document.removeEventListener(e, resume))
  }
  ;['click','keydown','touchstart','pointerdown'].forEach(e =>
    document.addEventListener(e, resume, { once: true, passive: true }))
}

// ── Public API ────────────────────────────────────────────────────────────────
export function toggleGlobalMusic() {
  const next = !isMusicMuted()
  try { localStorage.setItem(MUSIC_MUTED_KEY, next ? 'true' : 'false') } catch {}
  if (!_audio) return
  if (!next) {
    _audio.muted = false
    _audio.play().catch(() => {})
  } else {
    _audio.muted = true
  }
  notifyMute(next)
}

export function playTrack(idx) {
  if (idx < 0 || idx >= TRACKS.length) return
  _currentTrackIdx = idx
  _playlistMode    = true
  if (_audio) {
    _audio.src         = TRACKS[idx].src
    _audio.currentTime = 0
    if (!isMusicMuted()) _audio.play().catch(() => {})
  }
  notifyTrack(idx)
}

export function toggleShuffle() {
  const next = !isShuffleOn()
  try { localStorage.setItem(SHUFFLE_KEY, next ? 'true' : 'false') } catch {}
  if (next) buildPlaylist(_currentTrackIdx)
  return next
}

export function getCurrentTrackIdx() { return _currentTrackIdx }

// ── React hook ────────────────────────────────────────────────────────────────
export function useMusic(src) {
  const [muted,      setMuted]      = useState(isMusicMuted)
  const [trackIdx,   setTrackIdx]   = useState(() => _currentTrackIdx)
  const [shuffleOn,  setShuffleOn]  = useState(isShuffleOn)

  useEffect(() => {
    const isPlaylist = !src  // no src → playlist mode
    _playlistMode = isPlaylist

    const audio = getOrCreateAudio(src || TRACKS[_currentTrackIdx].src)
    ensureVisibilityListener()

    if (isPlaylist && _playlist.length === 0) {
      buildPlaylist()
      _currentTrackIdx = _playlist[0]
      audio.src = TRACKS[_currentTrackIdx].src
      notifyTrack(_currentTrackIdx)
    } else if (src && audio.src !== new URL(src, location.href).href) {
      // Login page: switch to specific track without affecting playlist state
      audio.src = src
      audio.currentTime = 0
    }

    if (audio.paused) {
      audio.play().catch(() => {
        audio.muted = true
        audio.play().catch(() => {})
        if (!isMusicMuted()) ensureAutoplayResume()
      })
    }
  }, [src])

  // Subscribe to mute changes
  useEffect(() => {
    const h = m => setMuted(m)
    _listeners.add(h)
    return () => _listeners.delete(h)
  }, [])

  // Subscribe to track changes
  useEffect(() => {
    const h = i => { setTrackIdx(i); setShuffleOn(isShuffleOn()) }
    _trackListeners.add(h)
    return () => _trackListeners.delete(h)
  }, [])

  return {
    muted,
    trackIdx,
    shuffleOn,
    currentTrack: TRACKS[trackIdx],
    toggleMute:   toggleGlobalMusic,
    playTrack,
    toggleShuffle: () => setShuffleOn(toggleShuffle()),
  }
}
